import { combineReducers } from 'redux';
import { call, put, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { CustomerRequest, CustomerRequestLocalized, GetCustomerRequestsResponse } from 'generated/bffCrm/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';

import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';

export interface CustomerRequestsPayload {
  assetRefNo?: string;
  caRefNo?: string;
  crType?: string;
  cuRefNo?: string;
  stateList?: string[];
  crSubType?: string;
  taxDocument?: string;
  forceCheck?: boolean;
}

/* STATE */
export interface CustomerRequestsState {
  data: CustomerRequest[];
  claimData: CustomerRequest[];
  canOpenClaim: boolean;
  localizedData: CustomerRequestLocalized[];
}

/* ACTION TYPES */
export enum CustomerRequestsActionTypes {
  GetCustomerRequests = '@@CustomerRequests/GET_CUSTOMER_REQUESTS',
  GetClaimCustomerRequests = '@@CustomerRequests/GET_CLAIM_CUSTOMER_REQUESTS',
}

/* ACTIONS */
export const getCustomerRequestsActions = createApiActionCreators(CustomerRequestsActionTypes.GetCustomerRequests);
export const getClaimCustomerRequestsActions = createApiActionCreators(
  CustomerRequestsActionTypes.GetClaimCustomerRequests
);

/* REDUCERS */
const initialState: CustomerRequestsState = {
  data: [],
  claimData: [],
  localizedData: [],
  canOpenClaim: false,
};

const data = createReducer(initialState.data, {
  [CustomerRequestsActionTypes.GetCustomerRequests]: {
    [RequestActionTypes.SUCCESS]: (state: CustomerRequest[], payload: GetCustomerRequestsResponse) =>
      payload.customerRequests,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const claimData = createReducer(initialState.claimData, {
  [CustomerRequestsActionTypes.GetClaimCustomerRequests]: {
    [RequestActionTypes.SUCCESS]: (state: CustomerRequest[], payload: GetCustomerRequestsResponse) =>
      payload.customerRequests,
    [RequestActionTypes.FAILURE]: () => [],
    [RequestActionTypes.REQUEST]: () => [],
  },
});

const canOpenClaim = createReducer(initialState.canOpenClaim, {
  [CustomerRequestsActionTypes.GetClaimCustomerRequests]: {
    [RequestActionTypes.SUCCESS]: () => true,
    [RequestActionTypes.FAILURE]: () => false,
    [RequestActionTypes.REQUEST]: () => false,
  },
});

const localizedData = createReducer(initialState.data, {
  [CustomerRequestsActionTypes.GetCustomerRequests]: {
    [RequestActionTypes.SUCCESS]: (state: CustomerRequestLocalized[], payload: GetCustomerRequestsResponse) =>
      payload.customerRequests,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

export default combineReducers<CustomerRequestsState>({
  data,
  claimData,
  localizedData,
  canOpenClaim,
});

/* SELECTORS */

export const selectCustomerRequest = (state: AppState) => state.customerRequest;
export const selectCustomerRequestData = (state: AppState): CustomerRequest[] => selectCustomerRequest(state).data;
export const selectCustomerRequestClaimData = (state: AppState): CustomerRequest[] =>
  selectCustomerRequest(state).claimData;
export const selectCanOpenClaim = (state: AppState): boolean => selectCustomerRequest(state).canOpenClaim;
export const selectCustomerRequestLocalizedData = (state: AppState): CustomerRequestLocalized[] =>
  selectCustomerRequest(state).localizedData;

/* SAGAS */
function* getCustomerRequest({ payload }: AppAction<CustomerRequestsPayload>) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.getCustomerRequests,
      yield getApiUrl(environment.urlConfig.crm),
      payload.assetRefNo,
      payload.caRefNo,
      payload.crType,
      payload.cuRefNo,
      payload.stateList,
      payload.crSubType,
      payload.taxDocument
    ),
    suppressError: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getCustomerRequestsActions.success(resp.data));
  } else {
    yield put(getCustomerRequestsActions.failure());
  }
}

function* getClaimCustomerRequest({ payload }: AppAction<CustomerRequestsPayload>) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.getCustomerRequests,
      yield getApiUrl(environment.urlConfig.crm),
      payload.assetRefNo,
      payload.caRefNo,
      payload.crType,
      payload.cuRefNo,
      payload.stateList,
      payload.crSubType,
      payload.taxDocument,
      payload.forceCheck
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getClaimCustomerRequestsActions.success(resp.data));
  } else {
    yield put(getClaimCustomerRequestsActions.failure());
  }
}

/* EXPORT */
export function* customerRequestsSaga() {
  yield takeLatest(
    createActionType(CustomerRequestsActionTypes.GetCustomerRequests, RequestActionTypes.REQUEST),
    getCustomerRequest
  );
  yield takeLatest(
    createActionType(CustomerRequestsActionTypes.GetClaimCustomerRequests, RequestActionTypes.REQUEST),
    getClaimCustomerRequest
  );
}
